import { useMemo, useState } from "react";
import { useToast } from "../../components/Toast";
import {
  buildAnswersIndex,
  buildQuestionCatalog,
  buildReportSections,
  buildSelectorBlocks,
  countAnsweredStudents,
} from "../../lib/activityReportModel";
import ActivityReportSelectorPanel from "./report/ActivityReportSelectorPanel";
import {
  ReportEmptyState,
  ReportError,
  ReportSkeleton,
} from "./report/ActivityReportStates";
import ActivityReportToolbar from "./report/ActivityReportToolbar";
import ActivityReportView from "./report/ActivityReportView";
import { useActivityReportData } from "./report/useActivityReportData";

export default function ActivityReport() {
  const { showError } = useToast();
  const { templates, progress, students, loading, failed, reload } =
    useActivityReportData();
  // `selected` es la selección APLICADA al informe; `draft` es la del panel
  // abierto, que solo se aplica al pulsar "Generar informe". Arranca vacía: no
  // hay preguntas marcadas por defecto.
  const [selected, setSelected] = useState(() => new Set());
  const [draft, setDraft] = useState(null);
  const [hasReport, setHasReport] = useState(false);
  const [groupBy, setGroupBy] = useState("alumno");
  const [hideEmpty, setHideEmpty] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const catalog = useMemo(() => buildQuestionCatalog(templates), [templates]);

  const answersIndex = useMemo(() => {
    const templatesById = new Map(templates.map((t) => [t.id, t]));
    return buildAnswersIndex(progress, templatesById, catalog.byTemplate);
  }, [templates, progress, catalog]);

  const blocks = useMemo(
    () => buildSelectorBlocks(templates, catalog.byTemplate),
    [templates, catalog]
  );

  const selectedQuestions = useMemo(
    () => catalog.questions.filter((q) => selected.has(q.questionId)),
    [catalog, selected]
  );

  // Sin informe generado no se construyen secciones: al entrar no hay trabajo.
  const sections = useMemo(
    () =>
      hasReport
        ? buildReportSections({
            groupBy,
            students,
            selectedQuestions,
            answersIndex,
            hideEmpty,
          })
        : [],
    [hasReport, groupBy, students, selectedQuestions, answersIndex, hideEmpty]
  );

  const counts = useMemo(
    () => countAnsweredStudents(students, selectedQuestions, answersIndex),
    [students, selectedQuestions, answersIndex]
  );

  const openPanel = () => setDraft(new Set(selected));
  const closePanel = () => setDraft(null);

  const applyDraft = () => {
    setSelected(new Set(draft));
    setHasReport(true);
    setDraft(null);
  };

  const toggleQuestion = (questionId) =>
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });

  const setMany = (questionIds, checked) =>
    setDraft((prev) => {
      const next = new Set(prev);
      for (const id of questionIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  // pdfmake pesa ~2 MB: se carga solo al exportar, no en el bundle inicial.
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { buildActivityReportPdf } = await import(
        "../../lib/activityReportPdf"
      );
      buildActivityReportPdf({
        groupBy,
        students,
        selectedQuestions,
        answersIndex,
        hideEmpty,
      });
    } catch {
      showError("No se pudo generar el PDF");
    }
    setDownloading(false);
  };

  if (loading) return <ReportSkeleton />;
  if (failed) return <ReportError onRetry={reload} />;

  return (
    <div className="space-y-4">
      {hasReport ? (
        <>
          <ActivityReportToolbar
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            hideEmpty={hideEmpty}
            onHideEmptyChange={setHideEmpty}
            counts={counts}
            canDownload={selectedQuestions.length > 0 && !downloading}
            downloading={downloading}
            onDownload={handleDownload}
            onEditSelection={openPanel}
          />

          <ActivityReportView
            sections={sections}
            emptyMessage="No hay respuestas para la selección actual."
          />
        </>
      ) : (
        <ReportEmptyState onOpenSelector={openPanel} />
      )}

      {draft && (
        <ActivityReportSelectorPanel
          blocks={blocks}
          selected={draft}
          totalCount={catalog.questions.length}
          onToggle={toggleQuestion}
          onSetMany={setMany}
          onCancel={closePanel}
          onGenerate={applyDraft}
        />
      )}
    </div>
  );
}
